import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { standService } from '../services/standService';
import i18n from "../i18n";

// ─── Async Thunks ────────────────────────────────────────────────────────────

export const fetchStandList = createAsyncThunk(
  'stand/fetchList',
  async (params, { rejectWithValue }) => {
    try {
      return await standService.getStandList(params);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchStandDetail = createAsyncThunk(
  'stand/fetchDetail',
  async (standId, { rejectWithValue }) => {
    try {
      return await standService.getStandDetail(standId);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateStandStatus = createAsyncThunk(
  'stand/updateStatus',
  async (payload, { rejectWithValue }) => {
    try {
      const { standId, fuarId, ...statusData } = payload;
      return await standService.updateStandStatus(standId, statusData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createStand = createAsyncThunk(
  'stand/create',
  async (formData, { rejectWithValue }) => {
    try {
      return await standService.createStand(formData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateStand = createAsyncThunk(
  'stand/update',
  async ({ standId, payload }, { rejectWithValue }) => {
    try {
      return await standService.updateStand(standId, payload);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteStand = createAsyncThunk(
  'stand/delete',
  async (standId, { rejectWithValue }) => {
    try {
      await standService.deleteStand(standId);
      return standId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteStandFile = createAsyncThunk(
  'stand/deleteFile',
  async ({ standId, fileId }, { rejectWithValue }) => {
    try {
      await standService.deleteStandFile(standId, fileId);
      return { standId, fileId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const uploadStandFile = createAsyncThunk(
  'stand/uploadFile',
  async ({ standId, file, fileTypeId }, { rejectWithValue }) => {
    try {
      return await standService.uploadStandFile(standId, file, fileTypeId);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const esleStandFirma = createAsyncThunk(
  'stand/esle',
  async (payload, { rejectWithValue }) => {
    try {
      return await standService.esleStandFirma(payload);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeStandFirmaMapping = createAsyncThunk(
  'stand/removeMapping',
  async ({ standId, firmaId, fuarId }, { rejectWithValue }) => {
    try {
      await standService.removeStandFirmaMapping(standId, firmaId, fuarId);
      return { standId, firmaId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ─── Initial State ───────────────────────────────────────────────────────────

const initialState = {
  // Liste
  list: [],
  loading: false,
  error: null,
  success: null,
  pagination: {
    currentPage: 0,
    pageSize: 25,
    totalElements: 0,
    totalPages: 0,
  },
  filters: {
    fuarId: null,
    salonId: null,
    status: null,
    search: '',
  },

  // Detay
  detail: {
    data: null,
    loading: false,
    error: null,
  },

  // Dosyalar (stand detay sayfasında ayrıca kullanılır)
  dosyalar: {
    items: [],
    loading: false,
    error: null,
  },

  // Activity Log
  activityLog: {
    items: [],
    loading: false,
    error: null,
  },

  // Durum güncelleme feedback
  statusUpdate: {
    loading: false,
    success: false,
    error: null,
  },

  // Firma eşleme feedback
  esle: {
    loading: false,
    success: false,
    error: null,
  },
};

// ─── Slice ───────────────────────────────────────────────────────────────────

const standSlice = createSlice({
  name: 'stand',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
      state.detail.error = null;
      state.statusUpdate.error = null;
      state.esle.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    clearStatusUpdate: (state) => {
      state.statusUpdate = { loading: false, success: false, error: null };
    },
    clearEsleState: (state) => {
      state.esle = { loading: false, success: false, error: null };
    },
    resetStandState: (state) => {
      state.list = [];
      state.loading = false;
      state.error = null;
      state.success = null;
      state.detail = { data: null, loading: false, error: null };
    },
  },
  extraReducers: (builder) => {
    // ── fetchStandList ──
    builder
      .addCase(fetchStandList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStandList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.content || action.payload;
        state.pagination = {
          currentPage: action.payload.pageable?.pageNumber || 0,
          pageSize: action.payload.pageable?.pageSize || 25,
          totalElements: action.payload.totalElements || 0,
          totalPages: action.payload.totalPages || 0,
        };
      })
      .addCase(fetchStandList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── fetchStandDetail ──
    builder
      .addCase(fetchStandDetail.pending, (state) => {
        state.detail.loading = true;
        state.detail.error = null;
      })
      .addCase(fetchStandDetail.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.data = action.payload;
        if (action.payload.dosyalar) {
          state.dosyalar.items = action.payload.dosyalar;
        }
        if (action.payload.activityLog) {
          state.activityLog.items = action.payload.activityLog;
        }
      })
      .addCase(fetchStandDetail.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.error = action.payload;
      });

    // ── updateStandStatus ──
    builder
      .addCase(updateStandStatus.pending, (state) => {
        state.statusUpdate.loading = true;
        state.statusUpdate.success = false;
        state.statusUpdate.error = null;
      })
      .addCase(updateStandStatus.fulfilled, (state, action) => {
        state.statusUpdate.loading = false;
        state.statusUpdate.success = true;
        const idx = state.list.findIndex((s) => s.standId === action.payload.standId);
        if (idx !== -1) state.list[idx] = { ...state.list[idx], ...action.payload };
        if (state.detail.data?.standId === action.payload.standId) {
          state.detail.data = { ...state.detail.data, ...action.payload };
        }
      })
      .addCase(updateStandStatus.rejected, (state, action) => {
        state.statusUpdate.loading = false;
        state.statusUpdate.error = action.payload;
      });

    // ── createStand ──
    builder
      .addCase(createStand.fulfilled, (state, action) => {
        state.list.unshift(action.payload);
        state.success = i18n.t("services:standSlice.standBasariylaOlusturuldu");
      })
      .addCase(createStand.rejected, (state, action) => {
        state.error = action.payload;
      });

    // ── updateStand ──
    builder
      .addCase(updateStand.fulfilled, (state, action) => {
        const idx = state.list.findIndex((s) => s.standId === action.payload.standId);
        if (idx !== -1) state.list[idx] = action.payload;
        if (state.detail.data?.standId === action.payload.standId) {
          state.detail.data = action.payload;
        }
        state.success = i18n.t("services:standSlice.standGuncellendi");
      })
      .addCase(updateStand.rejected, (state, action) => {
        state.error = action.payload;
      });

    // ── deleteStand ──
    builder
      .addCase(deleteStand.fulfilled, (state, action) => {
        state.list = state.list.filter((s) => s.standId !== action.payload);
        state.success = 'Stand silindi';
      })
      .addCase(deleteStand.rejected, (state, action) => {
        state.error = action.payload;
      });

    // ── deleteStandFile ──
    builder
      .addCase(deleteStandFile.pending, (state) => {
        state.dosyalar.loading = true;
      })
      .addCase(deleteStandFile.fulfilled, (state, action) => {
        state.dosyalar.loading = false;
        state.dosyalar.items = state.dosyalar.items.filter(
          (f) => f.id !== action.payload.fileId
        );
        if (state.detail.data?.dosyalar) {
          state.detail.data.dosyalar = state.detail.data.dosyalar.filter(
            (f) => f.id !== action.payload.fileId
          );
        }
      })
      .addCase(deleteStandFile.rejected, (state, action) => {
        state.dosyalar.loading = false;
        state.dosyalar.error = action.payload;
      });

    // ── uploadStandFile ──
    builder
      .addCase(uploadStandFile.pending, (state) => {
        state.dosyalar.loading = true;
      })
      .addCase(uploadStandFile.fulfilled, (state, action) => {
        state.dosyalar.loading = false;
        state.dosyalar.items.unshift(action.payload);
        if (state.detail.data) {
          if (!state.detail.data.dosyalar) state.detail.data.dosyalar = [];
          state.detail.data.dosyalar.unshift(action.payload);
        }
      })
      .addCase(uploadStandFile.rejected, (state, action) => {
        state.dosyalar.loading = false;
        state.dosyalar.error = action.payload;
      });

    // ── esleStandFirma ──
    builder
      .addCase(esleStandFirma.pending, (state) => {
        state.esle.loading = true;
        state.esle.success = false;
        state.esle.error = null;
      })
      .addCase(esleStandFirma.fulfilled, (state, action) => {
        state.esle.loading = false;
        state.esle.success = true;
        // Eger servis firma bilgisini donuyorsa detail'i guncelle
        if (action.payload && state.detail.data) {
          if (!state.detail.data.firmalar) state.detail.data.firmalar = [];
          const exists = state.detail.data.firmalar.find(
            (f) => f.firmaId === action.payload.firmaId
          );
          if (!exists && action.payload.firmaId) {
            state.detail.data.firmalar.push(action.payload);
          }
        }
      })
      .addCase(esleStandFirma.rejected, (state, action) => {
        state.esle.loading = false;
        state.esle.error = action.payload;
      });

    // ── removeStandFirmaMapping ──
    builder
      .addCase(removeStandFirmaMapping.fulfilled, (state, action) => {
        if (state.detail.data?.firmalar) {
          state.detail.data.firmalar = state.detail.data.firmalar.filter(
            (f) => f.firmaId !== action.payload.firmaId
          );
        }
      })
      .addCase(removeStandFirmaMapping.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  setFilters,
  clearError,
  clearSuccess,
  clearStatusUpdate,
  clearEsleState,
  resetStandState,
} = standSlice.actions;

export default standSlice.reducer;

